import { useState } from 'react'
import { useAuth } from '@/context/auth-context'
import { Navigate } from '@tanstack/react-router'
import TeacherStatsCard from '@/components/teacher/TeacherStatsCard'
import StudentListTable from '@/components/teacher/StudentListTable'
import DistributePointsModal from '@/components/teacher/DistributePointsModal'
import type { StudentBasicInfo } from '@/lib/teacher-queries'

const TeacherZone = () => {
  const { user } = useAuth()
  const [selectedStudent, setSelectedStudent] = useState<StudentBasicInfo | null>(null)
  const [isModalOpen, setIsModalOpen] = useState(false)

  // Redirect if not authenticated or not a teacher
  if (!user) {
    return <Navigate to="/sign-in" />
  }

  if (user.role !== 'teacher') {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="card bg-white shadow-xl">
          <div className="card-body text-center">
            <h2 className="card-title text-error justify-center">Access Denied</h2>
            <p>Only teachers can access the Teacher Zone.</p>
            <p className="text-sm text-base-content/60 mt-2">
              If you believe you should have teacher access, please contact an administrator.
            </p>
            <div className="card-actions justify-center mt-4">
              <button
                className="btn btn-primary"
                onClick={() => window.history.back()}
              >
                Go Back
              </button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  const handleDistributeClick = (student: StudentBasicInfo) => {
    setSelectedStudent(student)
    setIsModalOpen(true)
  }

  const handleCloseModal = () => {
    setIsModalOpen(false)
    // Small delay before clearing selected student to allow modal animation
    setTimeout(() => setSelectedStudent(null), 300)
  }

  return (
    <div className="min-h-screen bg-base-100 py-8">
      <div className="container mx-auto px-4 max-w-7xl">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-primary mb-2">Teacher Zone</h1>
          <p className="text-lg text-base-content/70">
            Welcome, {user.name || user.username}! Manage your students and distribute points.
          </p>
        </div>

        {/* Stats Card */}
        <div className="mb-8">
          <TeacherStatsCard />
        </div>

        {/* Students Table */}
        <StudentListTable onDistributeClick={handleDistributeClick} />

        {/* Distribute Points Modal */}
        <DistributePointsModal
          student={selectedStudent}
          isOpen={isModalOpen}
          onClose={handleCloseModal}
        />
      </div>
    </div>
  )
}

export default TeacherZone
